import { OpenHouse } from './open-house';
import { Point } from './point';
import { Room } from './room';
import { ResoPropertyType, Provider, ResoPropertySubType, StandardStatus, AddressType, ListingType } from '@zoocasa/go-search';
import type { ListingAgent, Property } from '@zoocasa/go-search';
import dayjs from 'dayjs';
import { camelizeKeys } from '@zoocasa/node-kit/objects/transform-keys';
import { AgreementTypes } from 'types/agreement-storage-types';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { cleanDashedString } from 'data/addresses';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { standardStatusFromJSON } from 'types/reso';
import { countryCodeFromProvinceOrState } from 'utils/province_or_state';
import { SiteLanguage } from 'types/locales';
import { mlsProviderIds } from 'constants/mlsProviderIds';
import { ListingDisplayPolicy } from 'domain/price';
import { normalizeClientAppOpenHouse, normalizeGoSearchOpenHouse } from 'data/listing/open-house/normalize-open-house';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ListingSerializableProperties = ReturnType<typeof Listing.prototype.toJSON>;
export type Miscellaneous = {
  acreage?: string | null;
  approxAge?: string | null;
  balcony?: string | null;
  bldgAmenities1Out?: string | null;
  bldgAmenities2Out?: string | null;
  bldgAmenities3Out?: string | null;
  bldgAmenities4Out?: string | null;
  bldgAmenities5Out?: string | null;
  bldgAmenities6Out?: string | null;
  buildingInsuranceIncluded?: boolean | null;
  cableTvIncluded?: boolean | null;
  cacIncluded?: boolean | null;
  centralVac?: boolean | null;
  commonElementIncluded?: boolean | null;
  condoExp?: string | null;
  condoTaxesIncluded?: boolean | null;
  corpNum?: string | number | null;
  ensuiteLaundry?: boolean | null;
  elevator?: boolean | null;
  fireplaceStove?: boolean | null;
  fireplacePresent?: boolean | null;
  garageSpaces?: string | null;
  garageParkSpaces?: string | null;
  laundryLevel?: string | null;
  heatIncluded?: boolean | null;
  hydroIncluded?: boolean | null;
  kitchens?: string | null;
  parkingFac?: string | null;
  parkingIncluded?: boolean | null;
  parkingLegalDesc1?: string | null;
  parkingSpaces?: string | number | null;
  parkingSpot1?: string | null;
  parkingType?: string | null;
  pets?: string | null;
  stories?: string | null;
  waterIncluded?: boolean | null;
};

export type ListingLanguage = SiteLanguage.English | SiteLanguage.French;

/** A localized tax entry (municipal/school), as provided per-language in locale data. */
export type LocalizedTaxInfo = {
  year?: string | null;
  amount?: string | null;
};

/**
 * A localized municipal-evaluation entry (assessment year + lot/building assessed values).
 * Both go-search and client-app expose the land value under the `lot` key.
 */
export type LocalizedEvaluationInfo = {
  year?: string | null;
  lot?: string | null;
  building?: string | null;
};

/**
 * Locale-sensitive listing content for a single language.
 *
 * Sourced from the provider's per-language locale data (e.g. Quebec/Centris listings).
 * Keys are camelized from the API's snake_case (`school_taxes` -> `schoolTaxes`).
 * All fields are optional/nullable since availability varies by provider and language.
 */
export type LocalizedListingContent = {
  type?: string | null;
  style?: string | null;
  extras?: string | null;
  floors?: string | null;
  zoning?: string | null;
  parking?: string | null;
  roofing?: string | null;
  driveway?: string | null;
  proximity?: string | null;
  dimensions?: string | null;
  exclusions?: string | null;
  description?: string | null;
  schoolTaxes?: LocalizedTaxInfo | null;
  waterSupply?: string | null;
  sewageSystem?: string | null;
  heatingEnergy?: string | null;
  heatingSystem?: string | null;
  municipalTaxes?: LocalizedTaxInfo | null;
  energyEfficiency?: string | null;
  bathroom?: string | null;
  rentalAppliances?: string | null;
  cupboard?: string | null;
  distinctiveFeatures?: string | null;
  garage?: string | null;
  basement?: string | null;
  equipmentAvailable?: string | null;
  siding?: string | null;
  landscaping?: string | null;
  topography?: string | null;
  windowType?: string | null;
  windows?: string | null;
  otherTaxes?: LocalizedTaxInfo | null;
  waterTaxes?: LocalizedTaxInfo | null;
  utilitiesTaxes?: LocalizedTaxInfo | null;
  /** Municipal evaluation (Centris col78/col79/col80: assessment year, land and building values). */
  evaluation?: LocalizedEvaluationInfo | null;
  /** Per-locale rooms with localized type/level (Centris `PIECES_UNITES`). */
  rooms?: Room[] | null;
  /** True when free-text fields for this locale were filled by AI translation. */
  isAiTranslated?: boolean;
};

export type LocaleData = {
  [language in ListingLanguage]?: LocalizedListingContent;
};

/**
 * Convert `Room` instances nested inside locale data into plain, JSON-serializable
 * objects. Needed because `getServerSideProps` rejects class instances, and
 * `Listing.toJSON` otherwise passes `localeData` through untouched.
 */
function serializeLocaleDataRooms(localeData: LocaleData): LocaleData {
  const result: LocaleData = {};
  for (const language of Object.keys(localeData) as ListingLanguage[]) {
    const content = localeData[language];
    if (!content) continue;
    result[language] = content.rooms
      ? { ...content, rooms: content.rooms.map(room => room.toJSON()) as unknown as Room[] }
      : content;
  }
  return result;
}

/**
 * Rehydrate plain room objects nested inside locale data back into {@link Room}
 * instances after a `toJSON`/`fromJSON` round-trip so consumers keep access to
 * instance methods such as {@link Room.size}.
 */
function deserializeLocaleDataRooms(localeData: LocaleData): LocaleData {
  const result: LocaleData = {};
  for (const language of Object.keys(localeData) as ListingLanguage[]) {
    const content = localeData[language];
    if (!content) continue;
    result[language] = content.rooms
      ? { ...content, rooms: (content.rooms as unknown as Record<string, unknown>[]).map(room => new Room(room)) }
      : content;
  }
  return result;
}

export type AgentInfo = {
  name: string;
  email: string;
  phone: string;
  photo: string;
  designation: {
    [language in ListingLanguage]?: string;
  };
  exp_member_key: number | null;
};

function listingAgentToAgentInfo(agent: ListingAgent): AgentInfo {
  return {
    name: agent.fullName ?? '',
    email: agent.email ?? '',
    phone: agent.phone ?? '',
    photo: agent.mediaUrl ?? '',
    designation: {
      ...(agent.designation?.en != null ? { [SiteLanguage.English]: agent.designation.en } : {}),
      ...(agent.designation?.fr != null ? { [SiteLanguage.French]: agent.designation.fr } : {}),
    },
    exp_member_key: agent.expAgentId != null ? Number(agent.expAgentId) : null,
  };
}

// Fields that should return empty string for French language
const _LOCALE_SENSITIVE_DYNAMIC_FIELDS = ['driveway', 'heat', 'basement', 'exterior', 'ac', 'heatingFuel'] as const;
type LocaleSensitiveDynamicField = typeof _LOCALE_SENSITIVE_DYNAMIC_FIELDS[number];

export default class Listing {
  id: number;
  type: string | null;
  isRental: boolean | null;
  price: number | null;
  soldPrice: number | null;
  status: string | null;
  addressPath: string | null;
  isStandardized: string;
  bedrooms: number;
  bathrooms: number;
  bedroomsPartial: number | null;
  bathroomsPartial: number | null;
  providerId: number | null;
  isVow: boolean;
  isItsoVow: boolean;
  isCrea: boolean;
  isImageReady: boolean;
  imageUrls: string[];
  thumbnailUrls: string[];
  position: Point | null;
  unitNumber: string | null;
  mlsNum: string | null;
  streetNumber: string;
  streetName: string;
  city: string | null;
  province: string;
  postalCode: string;
  openHouses: null | OpenHouse[];
  styleName: string;
  imageUrl: string | null;
  thumbnailUrl: string;
  parking: number | null;
  addressSlug: string;
  availableAt: string | null;
  #addedAt: Date | null;
  #expiredAt: Date | null;
  #updatedOn: Date | null;
  #soldAt: Date | null;
  #onMarketDate: Date | null;
  #daysActive: number | null;
  lastStatus: string | null;
  squareFootage: Record<'min' | 'max', number> | null;
  virtualTourUrl: string | null;
  levels: string | null;
  locker: string | null;
  maintenanceFees: number | null;
  taxes: number | null;
  garage: string | null;
  rooms: Room[] | null;
  lotFrontage: string | null;
  lotDepth: string | null;
  style: 'house' | 'condo' | 'land';
  brokerage: string | null;
  brokerageEmail: string | null;
  brokeragePhone: string | null;
  disclaimer: string | null;
  misc: Miscellaneous | null;
  neighbourhoodName: string | null;
  url: string | null;
  sourceMlsUrl: string | null;
  mlsStatus: string | null;
  imageDesc: string | null;
  termsOfUseRequired: boolean | null;
  statusId: number | null;
  imageDescs: Map<string, string> | null;
  neighbourhood: Record<'id', number> | null;
  pool: string | null;
  buyerInfo: {
    agentFirstName?: string | null;
    agentLastName?: string | null;
    officeName?: string | null;
  } | null;
  lastFetched: Date | null;
  expAgentId: number | null;
  displayAddress: boolean | null;
  standardStatus: StandardStatus | null;
  propertyType: typeof ResoPropertyType[keyof typeof ResoPropertyType] | null;
  propertySubType: typeof ResoPropertySubType[keyof typeof ResoPropertySubType] | null;
  listingAgent: string | null;
  listingAgentContact: string | null;
  originalMlsName: string | null;
  localeData: LocaleData | null;
  hidePriceHistory: boolean;
  hideMarketTime: boolean;
  hideAvm: boolean;
  isExpListing: boolean;

  #language: ListingLanguage;
  #localeSensitiveDynamicFields: Map<LocaleSensitiveDynamicField, string | null>;
  #agentsInfo: AgentInfo[] | null;
  #displayPolicy: ListingDisplayPolicy | null;
  #lotSize: string | null;

  private constructor(language: ListingLanguage = SiteLanguage.English) {
    this.#language = language;
    this.#localeSensitiveDynamicFields = new Map();
    this.#agentsInfo = null;
    this.#displayPolicy = null;
    this.#lotSize = null;
    this.#daysActive = null;
    this.hidePriceHistory = false;
    this.hideMarketTime = false;
    this.hideAvm = false;
    this.isExpListing = false;
  }

  get language() {
    return this.#language;
  }

  get isCondo() {
    return this.style === 'condo';
  }

  get isAvailable() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE || this.standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'available';
  }

  get isSold() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_CLOSED;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'not_available_sold';
  }

  get isInactive() {
    return !this.isAvailable && !this.isSold;
  }

  get isSoldWithoutSoldPrice() {
    return this.isSold && !this.soldPrice;
  }

  /**
   * Returns the display policy for this listing based on regional regulations.
   */
  get displayPolicy(): ListingDisplayPolicy {
    if (!this.#displayPolicy) {
      this.#displayPolicy = ListingDisplayPolicy.fromListing(this.province, this.isSold);
    }
    return this.#displayPolicy;
  }

  /**
   * Whether the sold price can be displayed based on regional display policies.
   */
  get canDisplaySoldPrice(): boolean {
    return this.displayPolicy.canDisplaySoldPrice;
  }

  get avgSqft() {
    const { min, max } = this.squareFootage || {};
    if (min && max) {
      return Math.ceil((min + max) / 2);
    } else if (min) {
      return min;
    } else if (max) {
      return max;
    } else {
      return null;
    }
  }

  /** @deprecated use `src/types/reso.ts#getStatusLabelKey` instead */
  get statusLabel() {
    let lastStatusLabel: string | null = null;
    if (this.lastStatus) {
      const lastStatusMappings: Record<string, string> = {
        Bom: 'Back on Market',
        Dft: 'Deal Fell Through',
        Exp: 'Expired',
        Ext: 'Extended',
        Lc: 'Leased Conditionally',
        Lsd: 'Leased',
        New: 'New',
        Pc: 'Price Change',
        Sc: 'Sold Conditionally',
        Sld: 'Sold',
        Sus: 'Suspended',
        Ter: 'Terminated',
      };
      lastStatusLabel = lastStatusMappings[this.lastStatus];
    }
    if (lastStatusLabel && lastStatusLabel !== 'New') {
      return lastStatusLabel;
    } else if (this.isAvailable) {
      return this.isRental ? 'For Rent' : 'For Sale';
    } else if (this.isSold) {
      return this.isRental ? 'Leased' : 'Sold';
    } else {
      return 'Inactive';
    }
  }

  get isPrivate() {
    return this.isVow;
  }

  get isPillar9Vow() {
    return this.providerId === Provider.PROVIDER_PILLAR9_VOW;
  }

  get isRebgvVow() {
    return this.providerId === Provider.PROVIDER_REBGV_VOW;
  }

  get isVancouverFeed() {
    return this.providerId === Provider.PROVIDER_REBGV_VOW || this.providerId === Provider.PROVIDER_REBGV_IDX;
  }

  get isTrebIdxInactive() {
    return this.providerId === Provider.PROVIDER_TREB_IDX && !this.isAvailable;
  }

  get miscellaneous() {
    return this.misc ? camelizeKeys(this.misc as unknown as Record<string, unknown>) as Record<string, string | number | boolean> : null;
  }

  get daysOnMarket() {
    if (this.hideMarketTime) return null;
    if (this.#daysActive != null) return this.#daysActive;
    if (this.#addedAt) {
      return dayjs().diff(dayjs(this.#addedAt), 'day');
    }
    return null;
  }

  get sqftLabel() {
    // FIXME: Inconsistent "no data" indicator. This uses '-' but domain value objects
    // (BedroomCount, BathroomCount, ParkingCount) use '--'. Should standardize across codebase.
    let sqft = '-';
    if (this.squareFootage) {
      const { min, max } = this.squareFootage;
      if (!min && max === 0 || !max && min === 0) {
        return sqft;
      }
      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      sqft = max === 499 ? '0-499' : (min && max && min === max) ? String(min) : [min, max].filter(item => item).join('-');
      sqft = `${sqft} sqft`;
    }
    return sqft;
  }

  get lotSize() {
    const { lotFrontage, lotDepth } = this;
    if (lotFrontage && lotDepth) {
      return `${lotFrontage} x ${lotDepth}`;
    }

    if (this.#lotSize) {
      return this.#lotSize;
    }
    
    // Fallback: If frontage/depth not available but we have acreage, display that
    const acreage = this.misc?.acreage;
    if (acreage) {
      const acres = parseFloat(acreage);
      if (!isNaN(acres) && acres > 0) {
        if (acres === 1) {
          return '1 acre';
        } else {
          return `${acres} acres`;
        }
      }
    }
    
    return null;
  }

  get homeType() {
    const condos = ['condo', 'condo-highrise', 'condo-lowrise', 'condo-new-development', 'condo-other'];
    const houses = ['house', 'house-detached', 'house-attached', 'house-semidetached', 'house-other'];
    const styleName: string | null = this.styleName;
    if (!styleName) {
      return null;
    } else if (condos.includes(styleName)) {
      return 'condo';
    } else if (styleName === 'townhouse') {
      return 'townhouse';
    } else if (houses.includes(styleName)) {
      return 'house';
    }
    return null;
  }

  get priceBySqft(): number | null {
    if (this.avgSqft && this.price) {
      return Math.floor(this.price / this.avgSqft);
    }
    return null;
  }

  get seoType() {
    let type = this.homeType?.split('-')[0];
    if (type) {
      type = type.replace(/\bhouse/i, 'House');
      return type[0].toUpperCase() + type.slice(1).toLowerCase();
    }
    return undefined;
  }

  get addressPathWithFallback() {
    if (!this.displayAddress) {
      return this.addressPath;
    }
    return cleanDashedString(this.addressPath);
  }

  /**
   * Alias of {@link addressSlug} under the name listing cards read it by
   * (`ListingCardData.slug`). `fromProperty` already maps go-search's `slug`
   * into `addressSlug`, so the two are the same value; without this, a card
   * built from a client-app listing falls back to the tail of its address path
   * and hands `FavouriteToggle` a slug no saved listing matches.
   */
  get slug() {
    return this.addressSlug;
  }

  get isSignInAndTermsOfUseRequired() {
    return this.termsOfUseRequired ? true : false;
  }

  get isTreb() {
    return !this.isCrea;
  }

  get agreementType() {
    if (this.isPillar9Vow){
      return AgreementTypes.PILLAR9_VOW;
    } else if (this.isRebgvVow){
      return AgreementTypes.REBGV_VOW;
    } else if (this.isItsoVow){
      return AgreementTypes.ITSO_VOW;
    } else if (this.isCrea){
      return AgreementTypes.CREA;
    }
    return undefined;
  }

  get isOffMarket() {
    if (this.standardStatus && this.standardStatus !== StandardStatus.UNRECOGNIZED) {
      return this.standardStatus === StandardStatus.STANDARD_STATUS_DELETE;
    }
    // fallback to status if standardStatus is not set
    return this.status === 'deleted';
  }

  getStreet = (isAuthenticated: boolean) => {
    return capitalizeWords(!isAuthenticated && this.isPrivate ? this.streetName : this.seoStreet);
  };

  get seoStreet() {
    const unitNumber = this.unitNumber || '';
    const streetAddress = this.streetAddress;
    const seoStreet = unitNumber.length > 0 ? `${unitNumber} - ${streetAddress}` : streetAddress;
    return seoStreet;
  }

  get streetAddress() {
    const { streetNumber, streetName } = this;
    return streetNumber ? `${streetNumber} ${streetName}` : streetName;
  }

  get countryCode() {
    return countryCodeFromProvinceOrState(this.province);
  }

  /**
   * All locale-sensitive listing content for the active language.
   *
   * Resolves against {@link language} exactly like {@link description} and {@link extras}.
   * Use this to read the richer per-locale fields (roofing, zoning, proximity, taxes, etc.)
   * whose names would otherwise collide with unrelated top-level members
   * (`type`, `style`, `parking`, `driveway`).
   */
  get localizedContent(): LocalizedListingContent | null {
    return this.localeData?.[this.language] ?? null;
  }

  /**
   * Rooms sourced from the active-language locale data.
   *
   * Only Centris listings carry per-locale rooms (localized type/level from the
   * `PIECES_UNITES` feed). Returns `null` when no locale rooms are available;
   * callers should fall back to top-level {@link rooms}.
   */
  get localizedRooms(): Room[] | null {
    return this.localizedContent?.rooms ?? null;
  }

  get extras() {
    return this.localizedContent?.extras ?? null;
  }

  get description() {
    return this.localizedContent?.description ?? null;
  }

  // Locale-sensitive content whose field names collide with unrelated top-level members.
  // Exposed with a `localized` prefix so they don't shadow `type`, `style`, `parking`,
  // `driveway`, `garage`, `basement`.

  get localizedType() {
    return this.localizedContent?.type ?? null;
  }

  get localizedStyle() {
    return this.localizedContent?.style ?? null;
  }

  get localizedParking() {
    return this.localizedContent?.parking ?? null;
  }

  get localizedDriveway() {
    return this.localizedContent?.driveway ?? null;
  }

  get localizedGarage() {
    return this.localizedContent?.garage ?? null;
  }

  get localizedBasement() {
    return this.localizedContent?.basement ?? null;
  }

  get agentsInfo(): (Omit<AgentInfo, 'designation'> & { designation: string })[] | null {
    if (!this.#agentsInfo) {
      return null;
    }
    return this.#agentsInfo.map(agent => ({
      ...agent,
      designation: agent.designation?.[this.language] ?? agent.designation?.[SiteLanguage.English] ?? '',
    }));
  }

  // Generic helper for locale-sensitive dynamic fields
  #getLocaleSensitiveDynamicField(field: LocaleSensitiveDynamicField): string | null {
    const value = this.#localeSensitiveDynamicFields.get(field);
    return this.#language === 'fr' ? '' : (value ?? null);
  }

  get driveway() {
    return this.#getLocaleSensitiveDynamicField('driveway');
  }

  get heat() {
    return this.#getLocaleSensitiveDynamicField('heat');
  }

  get basement() {
    return this.#getLocaleSensitiveDynamicField('basement');
  }

  get exterior() {
    return this.#getLocaleSensitiveDynamicField('exterior');
  }

  get ac() {
    return this.#getLocaleSensitiveDynamicField('ac');
  }

  get heatingFuel() {
    return this.#getLocaleSensitiveDynamicField('heatingFuel');
  }

  /**
   * Applies regional display restrictions to the listing data.
   * This method modifies the listing in place to comply with regional requirements.
   */
  applyListingRestrictions(): void {
    if (!this.displayPolicy.canDisplayTimestamps) {
      this.#addedAt = null;
      this.#expiredAt = null;
      this.#updatedOn = null;
      this.#soldAt = null;
      this.#onMarketDate = null;
      this.#daysActive = null;
    }
  }

  get addedAt(): Date | null {
    return this.#addedAt;
  }

  get expiredAt(): Date | null {
    return this.#expiredAt;
  }

  get updatedOn(): Date | null {
    return this.#updatedOn;
  }

  get soldAt(): Date | null {
    return this.#soldAt;
  }

  /** The MLS go-live date for a Coming Soon listing (from go-search `onMarketDate`). */
  get onMarketDate(): Date | null {
    return this.#onMarketDate;
  }

  toJSON() {
    return {
      id: this?.id ?? null,
      type: this?.type ?? null,
      isRental: this?.isRental ?? null,
      price: this?.price ?? null,
      soldPrice: this?.soldPrice ?? null,
      status: this?.status ?? null,
      addressPath: this?.addressPath ?? null,
      isStandardized: this?.isStandardized ?? null,
      bedrooms: this?.bedrooms ?? null,
      bathrooms: this?.bathrooms ?? null,
      bedroomsPartial: this?.bedroomsPartial ?? null,
      bathroomsPartial: this?.bathroomsPartial,
      providerId: this?.providerId ?? null,
      isVow: this?.isVow ?? null,
      isItsoVow: this?.isItsoVow ?? null,
      isCrea: this?.isCrea ?? null,
      isImageReady: this?.isImageReady ?? null,
      imageUrls: this?.imageUrls ?? null,
      thumbnailUrls: this?.thumbnailUrls ?? null,
      position: this?.position?.toJSON() ?? null,
      unitNumber: this?.unitNumber,
      mlsNum: this?.mlsNum ?? null,
      streetNumber: this?.streetNumber ?? null,
      streetName: this?.streetName ?? null,
      city: this?.city ?? null,
      province: this?.province ?? null,
      postalCode: this?.postalCode ?? null,
      openHouses: this?.openHouses?.map(r => r.toJSON()) ?? null,
      styleName: this?.styleName ?? null,
      imageUrl: this?.imageUrl ?? null,
      thumbnailUrl: this?.thumbnailUrl ?? null,
      parking: this?.parking ?? null,
      addressSlug: this?.addressSlug ?? null,
      availableAt: this?.availableAt ?? null,
      addedAt: this.#addedAt?.toISOString() ?? null,
      expiredAt: this.#expiredAt?.toISOString() ?? null,
      updatedOn: this.#updatedOn?.toISOString() ?? null,
      soldAt: this.#soldAt?.toISOString() ?? null,
      onMarketDate: this.#onMarketDate?.toISOString() ?? null,
      daysActive: this.#daysActive,
      lastStatus: this?.lastStatus ?? null,
      squareFootage: this?.squareFootage ?? null,
      virtualTourUrl: this?.virtualTourUrl ?? null,
      levels: this?.levels ?? null,
      locker: this?.locker ?? null,
      maintenanceFees: this?.maintenanceFees ?? null,
      taxes: this?.taxes ?? null,
      exterior: this.#localeSensitiveDynamicFields.get('exterior') ?? null,
      garage: this?.garage ?? null,
      driveway: this.#localeSensitiveDynamicFields.get('driveway') ?? null,
      heat: this.#localeSensitiveDynamicFields.get('heat') ?? null,
      ac: this.#localeSensitiveDynamicFields.get('ac') ?? null,
      basement: this.#localeSensitiveDynamicFields.get('basement') ?? null,
      heatingFuel: this.#localeSensitiveDynamicFields.get('heatingFuel') ?? null,
      rooms: this?.rooms?.map(r => r.toJSON()) ?? null,
      lotFrontage: this?.lotFrontage ?? null,
      lotDepth: this?.lotDepth ?? null,
      lotSize: this.#lotSize ?? null,
      style: this?.style ?? null,
      brokerage: this?.brokerage ?? null,
      brokerageEmail: this?.brokerageEmail ?? null,
      brokeragePhone: this?.brokeragePhone ?? null,
      disclaimer: this?.disclaimer ?? null,
      misc: this?.misc ?? null,
      neighbourhoodName: this?.neighbourhoodName ?? null,
      url: this?.url ?? null,
      sourceMlsUrl: this?.sourceMlsUrl ?? null,
      mlsStatus: this?.mlsStatus ?? null,
      imageDesc: this?.imageDesc ?? null,
      termsOfUseRequired: this?.termsOfUseRequired ?? null,
      statusId: this?.statusId ?? null,
      imageDescs: this?.imageDescs ? Object.fromEntries(this?.imageDescs) : null,
      neighbourhood: this?.neighbourhood ?? null,
      pool: this?.pool ?? null,
      buyerInfo: this?.buyerInfo ?? null,
      lastFetched: this?.lastFetched?.toISOString() ?? null,
      expAgentId: this?.expAgentId ?? null,
      displayAddress: this?.displayAddress ?? null,
      standardStatus: this?.standardStatus ?? null,
      propertyType: this?.propertyType ?? null,
      propertySubType: this?.propertySubType ?? null,
      listingAgent: this?.listingAgent ?? null,
      listingAgentContact: this?.listingAgentContact ?? null,
      originalMlsName: this?.originalMlsName ?? null,
      localeData: this?.localeData ? serializeLocaleDataRooms(this.localeData) : null,
      agentsInfo: this.#agentsInfo ?? null,
      language: this?.language ?? SiteLanguage.English,
      hidePriceHistory: this.hidePriceHistory,
      hideMarketTime: this.hideMarketTime,
      hideAvm: this.hideAvm,
      isExpListing: this.isExpListing,
    };
  }

  /**
   * Create a listing instance from a client-app listing object (received from the client-app JSON Api).
   *
   * @param clientAppListing The client-app listing object to create a listing from.
   * @returns A new listing instance.
   */
  static fromClientAppListing(clientAppListing: ClientAppListing, language: ListingLanguage = SiteLanguage.English) {
    if (!isClientAppListing(clientAppListing)) {
      throw new Error('Invalid listing');
    }
    const listing = new Listing(language);
    const camelizedListing = camelizeKeys(clientAppListing);
    const attributes = camelizedListing.attributes as Record<string, unknown>;
    const relationships = camelizedListing.relationships as Record<string, unknown>;
    const neighbourhood = relationships?.neighbourhood as Record<string, unknown> | null;
    const neighbourhoodData = neighbourhood && 'data' in neighbourhood ? neighbourhood.data as Record<string, unknown> : {};
    const buyerInfo = attributes.buyerInfo as Record<string, string> | null ?? null;

    const hasImageDescs = !!attributes?.imageDescs;
    const imageDescsMap = hasImageDescs ? new Map() : null;
    if (hasImageDescs) {
      const imageDescs = attributes.imageDescs as Record<string, string>;
      for (const key in imageDescs) {
        if (Object.hasOwnProperty.call(imageDescs, key)) {
          (imageDescsMap as Map<string, string>).set(key, imageDescs[key]);
        }
      }
    }
    listing.id = Number(camelizedListing.id);
    listing.type = attributes.type as string | null ?? null;
    listing.isRental = attributes.isRental as boolean | null ?? null;
    listing.price = Number(attributes.price);
    listing.soldPrice = attributes.soldPrice ? Number(attributes.soldPrice) : null;
    listing.status = attributes.status as string | null ?? null;
    listing.addressPath = attributes.addressPath as string | null ?? null;
    listing.isStandardized = attributes.isStandardized as string ?? null;
    listing.bedrooms = attributes.bedrooms as number ?? null;
    listing.bathrooms = attributes.bathrooms as number ?? null;
    listing.bedroomsPartial = attributes.bedroomsPartial as number | null ?? null;
    listing.bathroomsPartial = attributes.bathroomsPartial as number | null ?? null;
    listing.providerId = attributes.providerId as number | null ?? null;
    listing.isVow = attributes.isVow as boolean ?? null;
    listing.isItsoVow = attributes.isItsoVow as boolean ?? null;
    listing.isCrea = attributes.isCrea as boolean ?? null;
    listing.isImageReady = attributes.isImageReady as boolean ?? null;
    listing.imageUrls = attributes.imageUrls as string[] ?? null;
    listing.thumbnailUrls = attributes.thumbnailUrls as string[] ?? null;
    listing.position = attributes.position ? new Point(attributes.position as Record<string, unknown>) : null;
    listing.unitNumber = attributes.unitNumber as string | null ?? null;
    listing.mlsNum = attributes.mlsNum as string | null ?? null;
    listing.streetNumber = attributes.streetNumber as string ?? null;
    listing.streetName = attributes.streetName as string ?? null;
    listing.city = attributes.city as string | null ?? null;
    listing.province = attributes.province as string ?? null;
    listing.postalCode = attributes.postalCode as string ?? null;
    listing.openHouses = attributes.openHouses
      ? (attributes.openHouses as Record<string, unknown>[])?.map(o => new OpenHouse(normalizeClientAppOpenHouse(o)))
      : null;
    listing.styleName = attributes.styleName as string ?? null;
    listing.imageUrl = attributes.imageUrl as string | null ?? null;
    listing.thumbnailUrl = attributes.thumbnailUrl as string ?? null;
    listing.parking = attributes.parking ? Number(attributes.parking) : null;
    listing.addressSlug = attributes.addressSlug as string ?? null;
    listing.availableAt = attributes.availableAt as string | null ?? null;
    listing.#addedAt = new Date(attributes.addedAt as string);
    listing.#expiredAt = attributes.expiredAt ? new Date(attributes.expiredAt as string) : null;
    listing.#updatedOn = attributes.updatedOn ? new Date(attributes.updatedOn as string) : null;
    listing.#soldAt = attributes.soldAt ? new Date(attributes.soldAt as string) : null;
    listing.#onMarketDate = attributes.onMarketDate ? new Date(attributes.onMarketDate as string) : null;
    listing.#daysActive = attributes.daysActive != null ? Number(attributes.daysActive) : null;
    listing.lastStatus = attributes.lastStatus as string | null ?? null;
    listing.squareFootage = attributes.squareFootage as Record<'min' | 'max', number> | null ?? null;
    listing.virtualTourUrl = attributes.virtualTourUrl as string | null ?? null;
    listing.levels = attributes.levels as string | null ?? null;
    listing.locker = attributes.locker as string | null ?? null;
    listing.maintenanceFees = attributes.maintenanceFees as number | null ?? null;
    listing.taxes = attributes.taxes as number | null ?? null;
    listing.#localeSensitiveDynamicFields.set('exterior', attributes.exterior as string | null ?? null);
    listing.garage = attributes.garage as string | null ?? null;
    listing.#localeSensitiveDynamicFields.set('driveway', attributes.driveway as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('heat', attributes.heat as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('ac', attributes.ac as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('basement', attributes.basement as string | null ?? null);
    listing.#localeSensitiveDynamicFields.set('heatingFuel', attributes.heatingFuel as string | null ?? null);
    listing.rooms = attributes.rooms ? (attributes.rooms as Record<string, unknown>[])?.map(r => new Room(r)) : null;
    listing.lotFrontage = attributes.lotFrontage as string | null ?? null;
    listing.lotDepth = attributes.lotDepth as string | null ?? null;
    listing.#lotSize = attributes.lotSizeInfo as string | null ?? null;
    listing.style = attributes.style as 'house' | 'condo' | 'land';
    listing.brokerage = attributes.brokerage as string | null ?? null;
    listing.brokerageEmail = attributes.brokerageEmail as string | null ?? null;
    listing.brokeragePhone = attributes.brokeragePhone as string | null ?? null;
    listing.disclaimer = attributes.disclaimer as string | null ?? null;
    listing.misc = attributes.misc as Miscellaneous | null ?? null;
    listing.neighbourhoodName = attributes.neighbourhoodName as string | null ?? null;
    listing.url = attributes.url as string | null ?? null;
    listing.sourceMlsUrl = attributes.sourceMlsUrl as string | null ?? null;
    listing.mlsStatus = attributes.mlsStatus as string | null ?? null;
    listing.imageDesc = attributes.imageDesc as string | null ?? null;
    listing.termsOfUseRequired = attributes.termsOfUseRequired as boolean | null ?? null;
    listing.statusId = attributes.statusId as number | null ?? null;
    listing.imageDescs = imageDescsMap;
    listing.neighbourhood = { ...neighbourhoodData } as Record<'id', number>;
    listing.pool = attributes.pool as string | null ?? null;
    listing.buyerInfo = buyerInfo ? {
      agentFirstName: buyerInfo.agentFirstName as string | null ?? null,
      agentLastName: buyerInfo.agentLastName as string | null ?? null,
      officeName: buyerInfo.officeName as string | null ?? null,
    } : null;
    listing.lastFetched = attributes.lastFetched ? new Date(attributes.lastFetched as string) : null;
    listing.expAgentId = attributes.expAgentId as number | null ?? null;
    listing.displayAddress = attributes.displayAddress as boolean | null ?? null;
    listing.standardStatus = standardStatusFromJSON(attributes.standardStatus as string);
    listing.propertyType = attributes.propertyType as typeof ResoPropertyType[keyof typeof ResoPropertyType]| null;
    listing.propertySubType = attributes.propertySubType as typeof ResoPropertySubType[keyof typeof ResoPropertySubType] | null;
    listing.listingAgent = attributes.listingAgent as string | null ?? null;
    listing.listingAgentContact = attributes.listingAgentContact as string | null ?? null;
    listing.originalMlsName = attributes.originalMlsName as string | null ?? null;
    
    const agentsInfo = attributes.agentsInfo || attributes['agents-info'];
    listing.#agentsInfo = agentsInfo ? (agentsInfo as AgentInfo[]) : null;

    const extrasObj = typeof attributes.extras === 'object' && attributes.extras !== null ? attributes.extras as Record<string, unknown> : null;
    listing.hidePriceHistory = Boolean(attributes.hidePriceHistory ?? extrasObj?.hidePriceHistory);
    listing.hideMarketTime = Boolean(attributes.hideMarketTime ?? extrasObj?.hideMarketTime);
    listing.hideAvm = Boolean(attributes.hideAvm ?? extrasObj?.hideAvm);
    listing.isExpListing = Boolean(attributes.isExpListing);

    const rawLocaleData = attributes.localeData as LocaleData | null ?? null;
    // Rehydrate any plain room objects from the client-app JSON into Room instances so
    // consumers keep access to instance methods (e.g. Room.size).
    const localeData = rawLocaleData ? deserializeLocaleDataRooms(rawLocaleData) : null;

    // Check if this is a Centris listing
    const isCentrisListing = listing.providerId === mlsProviderIds.centris;

    // We assume that if a listing doesn't have the locale data it's not a french listing.
    // In that case, we use the extras and description from the attributes as the english extras and description to
    // keep the consistency of the data.
    // Exception: For Centris listings, we don't use fallback - if English locale data doesn't exist, it stays null.
    if (localeData) {
      if (!Object.hasOwnProperty.call(localeData, SiteLanguage.English)) {
        //TODO: CLEAN UP thIS CODE ONCE WE BACKFILL THE LOCALE DATA FOR ALL LISTINGS IN PROD
        if (isCentrisListing) {
          // For Centris, don't add English fallback - keep only the existing locale data
          listing.localeData = localeData;
        } else {
          // For other providers, use root attributes as English fallback
          listing.localeData = {
            ...localeData,
            [SiteLanguage.English]: {
              extras: attributes.extras as string | null ?? null,
              description: attributes.description as string | null ?? null,
            },
          };
        }
      } else {
        listing.localeData = localeData;
      }
    } else {
      if (isCentrisListing) {
        // For Centris with no locale data, set to null
        listing.localeData = null;
      } else {
        // For other providers, use root attributes as English data
        listing.localeData = {
          [SiteLanguage.English]: {
            extras: attributes.extras as string | null ?? null,
            description: attributes.description as string | null ?? null,
          },
        };
      }
    }
    listing.applyListingRestrictions();
    return listing;
  }

  /**
   * Create a listing instance from a go-search Property protobuf message.
   *
   * This factory method adapts the Property proto (from the /api/properties/* endpoints)
   * into the same Listing domain model used throughout the app, so downstream components
   * don't need to change.
   *
   * @param property The Property protobuf message from go-search.
   * @param language The language for locale-sensitive fields.
   * @returns A new Listing instance.
   */
  static fromProperty(property: Property, language: ListingLanguage = SiteLanguage.English): Listing {
    const listing = new Listing(language);

    listing.id = property.id;
    listing.mlsNum = property.mls || null;
    listing.addressSlug = property.slug;
    listing.postalCode = property.postalCode || '';

    // Status
    listing.standardStatus = property.status?.standardStatus ?? null;
    listing.statusId = null;
    listing.status = deriveStatusString(property.status?.standardStatus);
    listing.lastStatus = property.lastStatus || null;

    // Listing type
    listing.isRental = property.listingType === ListingType.LISTING_TYPE_RENT;

    // Property type
    listing.type = property.type?.typeOfProperty ?? null;
    listing.propertyType = property.type?.typeOfProperty as typeof ResoPropertyType[keyof typeof ResoPropertyType] ?? null;
    listing.propertySubType = property.type?.subTypeOfProperty as typeof ResoPropertySubType[keyof typeof ResoPropertySubType] ?? null;
    listing.style = deriveStyle(property.type?.typeOfProperty, property.type?.subTypeOfProperty);
    listing.styleName = deriveStyleName(property.type?.subTypeOfProperty);

    // Price
    listing.price = property.price?.listPrice ?? null;
    listing.soldPrice = property.sold?.soldPrice ?? null;

    // Bedrooms / Bathrooms
    listing.bedrooms = Number(property.bedrooms?.bedroomsTotal ?? 0);
    listing.bedroomsPartial = property.bedrooms?.bedroomsPossible != null
      ? Number(property.bedrooms.bedroomsPossible) - Number(property.bedrooms?.bedroomsTotal ?? 0)
      : null;
    // Per the RESO dictionary (dd.reso.org/DD1.7), feeds send either
    // BathroomsPartial (the sum of all half and one-quarter baths) or the
    // granular BathroomsHalf/BathroomsOneQuarter breakdown, never both.
    listing.bathroomsPartial = property.bathrooms?.bathroomsPartial != null
      ? Number(property.bathrooms.bathroomsPartial)
      : property.bathrooms?.bathroomsHalf != null || property.bathrooms?.bathroomsOneQuarter != null
        ? Number(property.bathrooms?.bathroomsHalf ?? 0) + Number(property.bathrooms?.bathroomsOneQuarter ?? 0)
        : null;
    // BathroomsTotalInteger is the simple sum of all bathrooms (RESO: 2 full +
    // 1 half = 3). When the feed omits it, sum the components; the two partial
    // schemes above are mutually exclusive, so nothing double-counts.
    listing.bathrooms = property.bathrooms?.bathroomsTotal != null
      ? Number(property.bathrooms.bathroomsTotal)
      : Number(property.bathrooms?.bathroomsFull ?? 0)
        + Number(property.bathrooms?.bathroomsThreeQuarter ?? 0)
        + Number(listing.bathroomsPartial ?? 0);

    // Square footage (IntegerRange -> { min, max })
    if (property.squareFootage) {
      const min = property.squareFootage.gte ?? property.squareFootage.gt ?? null;
      const max = property.squareFootage.lte ?? property.squareFootage.lt ?? null;
      listing.squareFootage = (min != null || max != null) ? { min: min ?? 0, max: max ?? 0 } : null;
    } else {
      listing.squareFootage = null;
    }

    // Position (string "lat,lng" -> Point)
    listing.position = parsePosition(property.position);

    // Address hierarchy
    const provinceEntry = property.addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE);
    const subDivisionEntry = property.addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_SUB_DIVISION);
    const neighbourhoodEntry = property.addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD);
    const addressEntry = property.addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_ADDRESS);
    const multiUnitEntry = property.addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_MULTI_UNIT);

    listing.province = provinceEntry?.provinceOrState?.toUpperCase() || '';
    listing.city = toTitleCase(subDivisionEntry?.label) || null;
    listing.neighbourhoodName = toTitleCase(neighbourhoodEntry?.label) || null;
    listing.unitNumber = property.unit || multiUnitEntry?.label || multiUnitEntry?.multiUnit || null;

    // Use direct proto fields when available, fall back to parsing from address label
    listing.streetNumber = property.streetNumber || '';
    listing.streetName = property.streetName || '';
    if (!listing.streetNumber && !listing.streetName) {
      const addressLabel = addressEntry?.label;
      const streetParts = parseStreetFromFullAddress(addressLabel || property.fullAddress);
      listing.streetNumber = streetParts.streetNumber;
      listing.streetName = streetParts.streetName;
    }

    // Build addressPath from address hierarchy
    listing.addressPath = buildAddressPath(property.addresses);

    // Provider / VOW / CREA
    listing.providerId = property.providerId;
    listing.isVow = VOW_PROVIDER_IDS.has(property.providerId);
    listing.isItsoVow = property.providerId === Provider.PROVIDER_ITSO_VOW;
    listing.isCrea = property.providerId === Provider.PROVIDER_CREA_DDF;

    // Display flags
    listing.displayAddress = property.display?.internetAddressDisplay ?? null;
    listing.termsOfUseRequired = property.display?.termsOfUseRequired ?? null;
    listing.hidePriceHistory = property.display?.hidePriceHistory ?? false;
    listing.hideMarketTime = property.display?.hideMarketTime ?? false;
    listing.hideAvm = property.display?.hideAvm ?? false;
    listing.isExpListing = Boolean(property.isExpListing);

    // Media -> image URLs
    const sortedMedia = [...property.media]
      .filter(m => m.url && (!m.category || m.category.toLowerCase() === 'photo'))
      .sort((a, b) => (a.orderSequence ?? 0) - (b.orderSequence ?? 0));
    listing.imageUrls = sortedMedia.map(m => m.url);
    listing.thumbnailUrls = sortedMedia.map(m => m.url);
    listing.imageUrl = sortedMedia[0]?.url || null;
    listing.thumbnailUrl = sortedMedia[0]?.url || '';
    listing.isImageReady = sortedMedia.length > 0;
    listing.imageDesc = sortedMedia[0]?.description?.short || sortedMedia[0]?.description?.long || null;
    listing.imageDescs = sortedMedia.length > 0
      ? new Map(sortedMedia.filter(m => m.description?.short || m.description?.long).map(m => [m.url, m.description?.short || m.description?.long || '']))
      : null;

    // Rooms
    listing.rooms = property.rooms?.length > 0
      ? property.rooms.map(r => new Room({
        type: r.type || null,
        level: r.level || null,
        width: r.width || null,
        length: r.length || null,
        unit: r.unit || null,
        dimensions: r.dimensions || null,
        flooring: r.flooring || null,
      }))
      : null;

    listing.openHouses = property.openHouse?.length > 0
      ? property.openHouse.map(oh => new OpenHouse(normalizeGoSearchOpenHouse({
        from: oh.from,
        to: oh.to,
        timezone: oh.timezone,
      })))
      : null;

    // Parking
    listing.parking = property.parking?.parkingSpaces != null ? Number(property.parking.parkingSpaces) : null;

    // Brokerage + listing agents — go-search: typed `Property.listingAgents` only (client-app uses `fromClientAppListing` + `attributes.agentsInfo`)
    const listingAgents = property.listingAgents ?? [];
    const primaryAgent = listingAgents[0];
    listing.brokerage = primaryAgent?.officeName ?? property.brokerage?.name ?? null;
    listing.brokerageEmail = property.brokerage?.email ?? null;
    listing.brokeragePhone = property.brokerage?.phone ?? null;

    listing.listingAgent = primaryAgent?.fullName?.trim() || null;
    listing.listingAgentContact = primaryAgent ? goSearchListingAgentPrimaryContact(primaryAgent) : null;
    listing.expAgentId = primaryAgent?.expAgentId != null ? Number(primaryAgent.expAgentId) : null;

    // Buyer agent
    listing.buyerInfo = property.buyerAgent ? {
      agentFirstName: property.buyerAgent.firstName ?? null,
      agentLastName: property.buyerAgent.lastName ?? null,
      officeName: property.buyerAgent.officeName ?? null,
    } : null;

    // Sold
    // soldPrice already set above from property.sold?.soldPrice

    // Virtual tour
    listing.virtualTourUrl = property.virtualTour?.unbrandedUrl || property.virtualTour?.brandedUrl || null;

    // Fees -> maintenance fees
    const maintenanceFee = property.fees?.find(f =>
      f.type?.toLowerCase().includes('maintenance') || f.type?.toLowerCase().includes('condo')
    );
    listing.maintenanceFees = maintenanceFee?.amount ?? null;

    // Tax
    listing.taxes = property.tax?.amount ?? null;

    // Build misc from available proto fields
    const misc: Partial<Miscellaneous> = {};
    if (property.yearBuilt?.yearBuilt != null) misc.approxAge = String(property.yearBuilt.yearBuilt);
    if (property.features?.storiesTotal != null) misc.stories = String(property.features.storiesTotal);
    if (property.features?.ensuiteLaundry != null) misc.ensuiteLaundry = property.features.ensuiteLaundry;
    const derivedLaundryLevel = property.features?.laundryLevel || (property.features?.ensuiteLaundry ? 'Ensuite' : null);
    if (derivedLaundryLevel) misc.laundryLevel = derivedLaundryLevel;
    if (property.parking?.garageSpaces != null) misc.garageSpaces = String(property.parking.garageSpaces);
    if (property.features?.fireplace != null) misc.fireplaceStove = property.features.fireplace;
    listing.misc = Object.keys(misc).length > 0 ? (misc as Miscellaneous) : null;

    // Features
    listing.garage = property.parking?.parkingFeatures
      ?.split(', ')
      .find(f => f.startsWith('Garage Type:'))
      ?.replace('Garage Type:', '')
      || (property.features?.garage ? 'Yes' : null);
    listing.pool = property.features?.pool ? 'Yes' : null;

    // Timestamps
    listing.#addedAt = property.createdAt ?? null;
    listing.#updatedOn = property.updatedOn ?? null;
    listing.#expiredAt = property.expiredAt ?? null;
    listing.#soldAt = property.soldAt ?? null;
    listing.#onMarketDate = property.onMarketDate ?? null;
    listing.#daysActive = property.daysActive ?? null;

    // Locale data
    const isCentrisListing = listing.providerId === mlsProviderIds.centris;
    if (property.localeData && Object.keys(property.localeData).length > 0) {
      listing.localeData = {};
      for (const [langKey, info] of Object.entries(property.localeData)) {
        const lang = langKey === 'fr' ? SiteLanguage.French : SiteLanguage.English;
        listing.localeData[lang] = goSearchLocaleInfoToContent(info);
      }
      if (!listing.localeData[SiteLanguage.English] && !isCentrisListing) {
        listing.localeData[SiteLanguage.English] = {
          description: property.description || null,
          extras: null,
        };
      }
    } else if (!isCentrisListing) {
      listing.localeData = {
        [SiteLanguage.English]: {
          description: property.description || null,
          extras: null,
        },
      };
    } else {
      listing.localeData = null;
    }

    // Property features from proto (new in 1.1.33)
    listing.levels = property.features?.storiesTotal != null ? String(property.features.storiesTotal) : null;
    listing.locker = property.features?.locker ? 'Yes' : null;
    listing.#localeSensitiveDynamicFields.set('heat', property.features?.heating ?? null);
    listing.#localeSensitiveDynamicFields.set('ac', property.features?.cooling ?? null);
    listing.#localeSensitiveDynamicFields.set('basement', property.features?.basementFeatures?.length > 0 ? property.features.basementFeatures.join(', ') : null);
    listing.#localeSensitiveDynamicFields.set('exterior', property.features?.exteriorFeatures?.length > 0 ? property.features.exteriorFeatures.join(', ') : null);

    // Fields not yet available in go-search Property proto
    listing.isStandardized = null;
    listing.availableAt = null;
    listing.lastFetched = property.sourceLastModified ?? null;
    listing.lotFrontage = null;
    listing.lotDepth = null;
    listing.#lotSize = property.features?.lotSize ?? null;
    listing.disclaimer = null;
    listing.neighbourhood = null;
    // client-app uses `url`; go-search exposes the CREA/more-info link as `more_information_link`
    listing.url = getGoSearchMoreInformationLink(property);
    listing.sourceMlsUrl = property.sourceMlsUrl ?? null;
    listing.mlsStatus = property.mlsStatus ?? null;
    listing.originalMlsName = property.originatingSystemName ?? null;

    // Agent info from go-search Property.listingAgents (repeated in proto)
    if (property.listingAgents?.length > 0) {
      listing.#agentsInfo = property.listingAgents.map(agent => listingAgentToAgentInfo(agent));
    } else {
      listing.#agentsInfo = null;
    }
    listing.#localeSensitiveDynamicFields.set('driveway', property.parking?.parkingFeatures ?? null);
    listing.#localeSensitiveDynamicFields.set('heatingFuel', null);

    listing.applyListingRestrictions();
    return listing;
  }

  /**
   * Create a listing instance from a json object serialized using {@link toJSON}.
   *
   * Use {@link Listing.fromClientAppListing} to create a listing from a client-app listing object (received from the client-app JSON Api).
   *
   * @param json The json object to create a listing from.
   * @returns A new listing instance.
   */
  static fromJSON(json: ListingSerializableProperties) {
    const listing = new Listing(json.language ?? SiteLanguage.English);
    listing.id = json.id;
    listing.type = json.type ?? null;
    listing.isRental = json.isRental ?? null;
    listing.price = json.price ?? null;
    listing.soldPrice = json.soldPrice ?? null;
    listing.status = json.status ?? null;
    listing.addressPath = json.addressPath ?? null;
    listing.isStandardized = json.isStandardized;
    listing.bedrooms = json.bedrooms;
    listing.bathrooms = json.bathrooms;
    listing.bedroomsPartial = json.bedroomsPartial ?? null;
    listing.bathroomsPartial = json.bathroomsPartial ?? null;
    listing.providerId = json.providerId ?? null;
    listing.isVow = json.isVow;
    listing.isItsoVow = json.isItsoVow;
    listing.isCrea = json.isCrea;
    listing.isImageReady = json.isImageReady;
    listing.imageUrls = json.imageUrls;
    listing.thumbnailUrls = json.thumbnailUrls;
    listing.position = json.position ? new Point(json.position) : null;
    listing.unitNumber = json.unitNumber ?? null;
    listing.mlsNum = json.mlsNum ?? null;
    listing.streetNumber = json.streetNumber;
    listing.streetName = json.streetName;
    listing.city = json.city ?? null;
    listing.province = json.province;
    listing.postalCode = json.postalCode;
    listing.openHouses = json.openHouses ? (json.openHouses)?.map(r => new OpenHouse(r)) : null;
    listing.styleName = json.styleName;
    listing.imageUrl = json.imageUrl ?? null;
    listing.thumbnailUrl = json.thumbnailUrl;
    listing.parking = json.parking ?? null;
    listing.addressSlug = json.addressSlug;
    listing.availableAt = json.availableAt ?? null;
    listing.#addedAt = json.addedAt ? new Date(json.addedAt as string) : null;
    listing.#expiredAt = json.expiredAt ? new Date(json.expiredAt as string) : null;
    listing.#updatedOn = json.updatedOn ? new Date(json.updatedOn as string) : null;
    listing.#soldAt = json.soldAt ? new Date(json.soldAt as string) : null;
    listing.#onMarketDate = json.onMarketDate ? new Date(json.onMarketDate as string) : null;
    listing.#daysActive = json.daysActive != null ? Number(json.daysActive) : null;
    listing.lastStatus = json.lastStatus;
    listing.squareFootage = json.squareFootage ?? null;
    listing.virtualTourUrl = json.virtualTourUrl ?? null;
    listing.levels = json.levels ?? null;
    listing.locker = json.locker ?? null;
    listing.maintenanceFees = json.maintenanceFees ?? null;
    listing.taxes = json.taxes ?? null;
    listing.#localeSensitiveDynamicFields.set('exterior', json.exterior ?? null);
    listing.garage = json.garage ?? null;
    listing.#localeSensitiveDynamicFields.set('driveway', json.driveway ?? null);
    listing.#localeSensitiveDynamicFields.set('heat', json.heat ?? null);
    listing.#localeSensitiveDynamicFields.set('ac', json.ac ?? null);
    listing.#localeSensitiveDynamicFields.set('basement', json.basement ?? null);
    listing.#localeSensitiveDynamicFields.set('heatingFuel', json.heatingFuel ?? null);
    listing.rooms = json.rooms ? (json.rooms)?.map(r => new Room(r)) : null;
    listing.lotFrontage = json.lotFrontage ?? null;
    listing.lotDepth = json.lotDepth ?? null;
    listing.#lotSize = json.lotSize ?? null;
    listing.style = json.style;
    listing.brokerage = json.brokerage ?? null;
    listing.brokerageEmail = json.brokerageEmail ?? null;
    listing.brokeragePhone = json.brokeragePhone ?? null;
    listing.disclaimer = json.disclaimer ?? null;
    listing.misc = json.misc ?? null;
    listing.neighbourhoodName = json.neighbourhoodName ?? null;
    listing.url = json.url ?? null;
    listing.sourceMlsUrl = json.sourceMlsUrl ?? null;
    listing.mlsStatus = json.mlsStatus ?? null;
    listing.imageDesc = json.imageDesc ?? null;
    listing.termsOfUseRequired = json.termsOfUseRequired ?? null;
    listing.statusId = json.statusId ?? null;
    listing.imageDescs = json.imageDescs ? new Map(Object.entries(json.imageDescs)) : null;
    listing.neighbourhood = json.neighbourhood ?? null;
    listing.pool = json.pool ?? null;
    listing.buyerInfo = json.buyerInfo ? {
      agentFirstName: json.buyerInfo.agentFirstName ?? null,
      agentLastName: json.buyerInfo.agentLastName ?? null,
      officeName: json.buyerInfo.officeName ?? null,
    } : null;
    listing.lastFetched = json.lastFetched ? new Date(json.lastFetched as string) : null;
    listing.expAgentId = json.expAgentId ?? null;
    listing.displayAddress = json.displayAddress ?? null;
    listing.standardStatus = json.standardStatus ?? null;
    listing.propertyType = json.propertyType ?? null;
    listing.propertySubType = json.propertySubType ?? null;
    listing.listingAgent = json.listingAgent ?? null;
    listing.listingAgentContact = json.listingAgentContact ?? null;
    listing.originalMlsName = json.originalMlsName ?? null;
    listing.localeData = json.localeData ? deserializeLocaleDataRooms(json.localeData) : null;
    listing.#agentsInfo = json.agentsInfo ? (json.agentsInfo as AgentInfo[]) : null;
    listing.hidePriceHistory = json.hidePriceHistory ?? false;
    listing.hideMarketTime = json.hideMarketTime ?? false;
    listing.hideAvm = json.hideAvm ?? false;
    listing.isExpListing = json.isExpListing ?? false;

    // If we are reading a listing that doesn't have the locale data,
    // we assume it was serialized before localeData was added to Listing.
    // So we try to use the extras and description from the json object
    // to keep the consistency of the data.
    if (!listing.localeData) {
      listing.localeData = {
        [SiteLanguage.English]: {
          extras: (json as Record<string, unknown>).extras as string | null ?? null,
          description: (json as Record<string, unknown>).description as string | null ?? null,
        },
      };
    }
    listing.applyListingRestrictions();
    return listing;
  }
}

export type ClientAppListing = {
  id: string;
  type: 'listings';
  attributes?: Record<string, unknown>;
  relationships?: Record<string, unknown>;
};

/**
 * Check if an object has the properties associated with a client-app listing object (received from the client-app JSON Api).
 *
 * @param obj The object to check.
 * @returns `true` if the object is a client-app listing, `false` otherwise.
 */
export function isClientAppListing(obj: unknown): obj is ClientAppListing {
  return obj && typeof obj === 'object' && 'type' in obj && obj.type === 'listings';
}

//#region fromProperty helpers

/** The go-search proto `LocaleInfo` shape, derived from `Property.localeData` (not re-exported at the package root). */
type GoSearchLocaleInfo = Property['localeData'][string];
type GoSearchCentrisTax = NonNullable<GoSearchLocaleInfo['municipalTaxes']>;
type GoSearchCentrisEvaluation = NonNullable<GoSearchLocaleInfo['evaluation']>;

/**
 * go-search CREA/more-info link (`more_information_link` in proto JSON).
 * Narrowed here until `@zoocasa/go-search` exports it on `Property`.
 */
type PropertyWithMoreInformationLink = Property & { moreInformationLink?: string | null };

function getGoSearchMoreInformationLink(property: Property): string | null {
  return (property as PropertyWithMoreInformationLink).moreInformationLink ?? null;
}

/**
 * Join a repeated (array) locale label into the comma-separated string the domain expects.
 * go-search returns these as `string[]`, while the client-app JSON delivers pre-joined strings.
 */
function joinLocaleLabels(values: string[] | undefined): string | null {
  return values && values.length > 0 ? values.join(', ') : null;
}

/** Map a go-search proto `CentrisTax` into the domain {@link LocalizedTaxInfo} shape. */
function goSearchTaxToLocalizedTax(tax: GoSearchCentrisTax | undefined): LocalizedTaxInfo | null {
  if (!tax) return null;
  return { year: tax.year ?? null, amount: tax.amount ?? null };
}

/** Map a go-search proto `CentrisEvaluation` into the domain {@link LocalizedEvaluationInfo} shape. */
function goSearchEvaluationToLocalizedEvaluation(
  evaluation: GoSearchCentrisEvaluation | undefined
): LocalizedEvaluationInfo | null {
  if (!evaluation) return null;
  return {
    year: evaluation.year ?? null,
    lot: evaluation.lot ?? null,
    building: evaluation.building ?? null,
  };
}

/** Map a go-search proto `LocaleInfo` into the domain {@link LocalizedListingContent} shape. */
function goSearchLocaleInfoToContent(info: GoSearchLocaleInfo): LocalizedListingContent {
  return {
    type: info.type ?? null,
    style: info.style ?? null,
    extras: info.inclusions ?? null,
    floors: info.floors ?? null,
    zoning: joinLocaleLabels(info.zoning),
    parking: joinLocaleLabels(info.parking),
    roofing: joinLocaleLabels(info.roofing),
    driveway: joinLocaleLabels(info.driveway),
    proximity: joinLocaleLabels(info.proximity),
    dimensions: info.dimensions ?? null,
    exclusions: info.exclusions ?? null,
    description: info.description ?? null,
    schoolTaxes: goSearchTaxToLocalizedTax(info.schoolTaxes),
    waterSupply: joinLocaleLabels(info.waterSupply),
    sewageSystem: joinLocaleLabels(info.sewageSystem),
    heatingEnergy: joinLocaleLabels(info.heatingEnergy),
    heatingSystem: joinLocaleLabels(info.heatingSystem),
    municipalTaxes: goSearchTaxToLocalizedTax(info.municipalTaxes),
    energyEfficiency: joinLocaleLabels(info.energyEfficiency),
    bathroom: joinLocaleLabels(info.bathroom),
    rentalAppliances: joinLocaleLabels(info.rentalAppliances),
    cupboard: joinLocaleLabels(info.cupboard),
    distinctiveFeatures: joinLocaleLabels(info.distinctiveFeatures),
    garage: joinLocaleLabels(info.garage),
    basement: joinLocaleLabels(info.basement),
    equipmentAvailable: joinLocaleLabels(info.equipmentAvailable),
    siding: joinLocaleLabels(info.siding),
    landscaping: joinLocaleLabels(info.landscaping),
    topography: joinLocaleLabels(info.topography),
    windowType: joinLocaleLabels(info.windowType),
    windows: joinLocaleLabels(info.windows),
    otherTaxes: goSearchTaxToLocalizedTax(info.otherTaxes),
    waterTaxes: goSearchTaxToLocalizedTax(info.waterTaxes),
    utilitiesTaxes: goSearchTaxToLocalizedTax(info.utilitiesTaxes),
    evaluation: goSearchEvaluationToLocalizedEvaluation(info.evaluation),
    isAiTranslated: (info as GoSearchLocaleInfo & { isAiTranslated?: boolean }).isAiTranslated === true,
    rooms: info.rooms?.length > 0
      ? info.rooms.map(r => new Room({
        type: r.type || null,
        level: r.level || null,
        width: r.width || null,
        length: r.length || null,
        unit: r.unit || null,
        dimensions: r.dimensions || null,
        flooring: r.flooring || null,
      }))
      : null,
  };
}

/** One-line contact for `listingAgentContact` (go-search proto: contact, then phone, then email). */
function goSearchListingAgentPrimaryContact(agent: ListingAgent): string | null {
  const contact = agent.contact?.trim();
  if (contact) return contact;
  const phone = agent.phone?.trim();
  if (phone) return phone;
  const email = agent.email?.trim();
  return email || null;
}

const VOW_PROVIDER_IDS: ReadonlySet<Provider> = new Set([
  Provider.PROVIDER_TREB_VOW,
  Provider.PROVIDER_OREB_VOW,
  Provider.PROVIDER_ITSO_VOW,
  Provider.PROVIDER_PILLAR9_VOW,
  Provider.PROVIDER_REBGV_VOW,
  Provider.PROVIDER_VICTORIA_AND_VANCOUVER_ISLAND_BOARD_VOW,
  Provider.PROVIDER_NSAR_VOW,
]);

function deriveStatusString(standardStatus: StandardStatus | null | undefined): string | null {
  switch (standardStatus) {
    case StandardStatus.STANDARD_STATUS_ACTIVE:
    case StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT:
    case StandardStatus.STANDARD_STATUS_COMING_SOON:
      return 'available';
    case StandardStatus.STANDARD_STATUS_CLOSED:
      return 'not_available_sold';
    case StandardStatus.STANDARD_STATUS_EXPIRED:
    case StandardStatus.STANDARD_STATUS_CANCELED:
    case StandardStatus.STANDARD_STATUS_WITHDRAWN:
      return 'not_available_other';
    case StandardStatus.STANDARD_STATUS_PENDING:
    case StandardStatus.STANDARD_STATUS_HOLD:
      return 'pending';
    case StandardStatus.STANDARD_STATUS_DELETE:
      return 'deleted';
    default:
      return null;
  }
}

function deriveStyle(
  typeOfProperty: string | undefined,
  subTypeOfProperty: string | undefined
): 'house' | 'condo' | 'land' {
  const sub = (subTypeOfProperty || '').toLowerCase();
  const type = (typeOfProperty || '').toLowerCase();
  if (sub.includes('condo') || sub.includes('apartment')) return 'condo';
  if (type.includes('land') || type.includes('vacant')) return 'land';
  return 'house';
}

function deriveStyleName(subTypeOfProperty: string | undefined): string {
  const sub = (subTypeOfProperty || '').toLowerCase();
  if (sub.includes('condo') && sub.includes('high')) return 'condo-highrise';
  if (sub.includes('condo') && sub.includes('low')) return 'condo-lowrise';
  if (sub.includes('condo') || sub.includes('apartment')) return 'condo';
  if (sub.includes('townhouse') || sub.includes('town')) return 'townhouse';
  if (sub.includes('semi') || sub.includes('duplex')) return 'house-semidetached';
  if (sub.includes('attach')) return 'house-attached';
  if (sub.includes('detach') || sub.includes('single')) return 'house-detached';
  return 'house';
}

/**
 * Parse a geo position string into a Point.
 *
 * Supported formats:
 *   - WKT:   "POINT(lng lat)"  — used by go-search / listings_v2
 *   - CSV:   "lat,lng"         — legacy client-app format
 *
 * Returns null if the string is empty or unparseable.
 */
function toTitleCase(str: string | undefined | null): string | null {
  if (!str) return null;
  return str.replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function parsePosition(positionStr: string | undefined): Point | null {
  if (!positionStr) return null;

  const wktMatch = positionStr.match(/^POINT\(\s*([-\d.]+)\s+([-\d.]+)\s*\)$/i);
  if (wktMatch) {
    const lng = parseFloat(wktMatch[1]);
    const lat = parseFloat(wktMatch[2]);
    if (isNaN(lng) || isNaN(lat)) return null;
    return new Point({ type: 'Point', coordinates: [lng, lat]});
  }

  if (positionStr.includes(',')) {
    const parts = positionStr.split(',').map(s => parseFloat(s.trim()));
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    const [lat, lng] = parts;
    return new Point({ type: 'Point', coordinates: [lng, lat]});
  }

  return null;
}

/**
 * Parse street number and street name from the full address string.
 * Full address format: "1008-120 St Patrick St, Toronto, ON, M5T2X7"
 */
function parseStreetFromFullAddress(fullAddress: string): { streetNumber: string; streetName: string } {
  if (!fullAddress) return { streetNumber: '', streetName: '' };
  const streetPart = fullAddress.split(',')[0]?.trim() || '';
  const match = streetPart.match(/^(\S+)\s+(.+)$/);
  if (match) {
    return { streetNumber: match[1], streetName: match[2] };
  }
  return { streetNumber: '', streetName: streetPart };
}

/**
 * Build the URL address path from a SingleAddress hierarchy.
 * Produces paths like "/toronto-on-real-estate/some-neighbourhood/123-main-st"
 *
 * The subdivision slug from go-search may not include the province abbreviation
 * (e.g. "milton" instead of "milton-on"), so we derive it from provinceOrState.
 */
function buildAddressPath(addresses: { addressType: AddressType; slug: string; provinceOrState?: string }[]): string | null {
  if (!addresses || addresses.length === 0) return null;
  const subDiv = addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_SUB_DIVISION);
  const neighbourhood = addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD);
  const address = addresses.find(a => a.addressType === AddressType.ADDRESS_TYPE_ADDRESS);

  if (!subDiv?.slug) return null;

  const province = subDiv.provinceOrState?.toLowerCase();
  const subDivSlug = province && !subDiv.slug.endsWith(`-${province}`)
    ? `${subDiv.slug}-${province}`
    : subDiv.slug;

  let path = `/${subDivSlug}-real-estate`;
  if (neighbourhood?.slug) {
    path += `/${neighbourhood.slug.replace(`-${subDivSlug}`, '')}`;
  }
  if (address?.slug) {
    path += `/${address.slug.replace(`-${subDivSlug}`, '')}`;
  }
  return path;
}

//#endregion
